// Time-series weather observations for a single station or model grid point

import chroma from 'chroma-js';
import { distanceM } from '../utils/geometry-util.js';
// const windSpeedColors = chroma
//   .scale(['#908f8f', '#000099', '#007700', '#CCCC00', '#FF8C00', '#990000', '#8B0000', '#A52A2A', '#3B1500', '#000000'])
//   .classes([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 200]);

// const windSpeedColors = chroma
//   .scale(['#908f8f', '#000099', '#CCCC00', '#990000', '#000000'])
//   .classes([0, 10, 20, 30, 40, 50]);

 const windSpeedColors = chroma
   .scale(['#908f8f', '#000099', '#CCCC00', '#990000', '#000000'])
   .domain([0, 50]);


export class StationSeries {

  constructor(id, name, lat, lon, source, score = 500, hidden = false) {
    this.id = id;           // WMO number, ICAO code, or 'model:lat,lon'
    this.name = name;
    this.lat = lat;
    this.lon = lon;
    this.source = source;   // 'metservice-synop' | 'iem-asos' | 'open-meteo'
    this.score = score;     // relevance score for map declutter (spec 24 D9); lower = more relevant. Only ZephyrProvider sets a real value.
    this.hidden = hidden;   // user's manual visibility toggle (spec 25 N2), persisted across sessions
    this.observations = [];
  }

  addObservation(obs) {
    this.observations.push(obs);
  }

  sortObservations() {
    this.observations.sort((a, b) => a.t < b.t ? -1 : 1);
  }

  obsAt(t) {
    if (!this.observations.length) return null;
    let result = null;
    for (const o of this.observations) {
      if (o.t <= t) result = o;
      else break;
    }
    return result;
  }

  isHidden() {
    return this.hidden || this.observations == null || this.observations?.length == 0;
  }

  setHidden(flag) {
    this.hidden = flag;
  }

  sameStation(other) {
    if (this.source === other.source) return this.id.toLowerCase() === other.id.toLowerCase();
    if (this.id.toLowerCase() === other.id.toLowerCase()) return true;
    if (this.name.toLowerCase() === other.name.toLowerCase()) return true;
    if (this.lat == null || this.lon == null || other.lat == null || other.lon == null) return false;
    return distanceM(this, other) <= 300;
  }

  merge(other) {
    const baseMin = this.observations.length ? this.observations[0].t : Infinity;
    const baseMax = this.observations.length ? this.observations[this.observations.length - 1].t : -Infinity;
    const extensions = other.observations.filter(o => o.t < baseMin || o.t > baseMax);
    // score carries forward from `this`, never `other` — WeatherService.#mergeAll sorts merge
    // groups by provider priority before reducing, so `this` is always the higher-priority side;
    // as long as Zephyr stays priority 0 in createDefaultWeatherService, its real score always wins (spec 25 N11)
    const merged = new StationSeries(this.id, this.name, this.lat, this.lon, this.source, this.score, this.hidden);
    merged.observations = [...this.observations, ...extensions];
    merged.sortObservations();
    if (extensions.length > 0) merged.source = `${this.source}/${other.source}`;
    return merged;
  }

  serialiseToObj() {
    return {
      id: this.id,
      name: this.name,
      lat: this.lat,
      lon: this.lon,
      source: this.source,
      score: this.score,
      hidden: this.hidden,
      observations: this.observations.map(o => o.serialiseToObj()),
    };
  }

  serialiseToJSON() {
    return JSON.stringify(this.serialiseToObj());
  }

  static serialiseToObj(stations) {
    return stations.map(s => s.serialiseToObj());
  }
  
  static fromObjects(stations) {
    return stations.map(obj => StationSeries.fromObj(obj));
  }

  static fromObj(obj) {
    const s = new StationSeries(obj.id, obj.name, obj.lat, obj.lon, obj.source, obj.score, obj.hidden);
    s.observations = (obj.observations ?? []).map(o => new Observation(o.t, o.dd, o.ff, o.tt, o.mslp, o.gust));
    return s;
  }

  static groupByDay(stations) {
    const dayMap = new Map();
    for (const station of stations) {
      for (const obs of station.observations) {
        const day = new Date(obs.t).toISOString().slice(0, 10);
        if (!dayMap.has(day)) dayMap.set(day, new Map());
        const dayStations = dayMap.get(day);
        if (!dayStations.has(station.id)) {
          dayStations.set(station.id, new StationSeries(station.id, station.name, station.lat, station.lon, station.source));
        }
        dayStations.get(station.id).addObservation(obs);
      }
    }
    const result = new Map();
    for (const [day, stationMap] of dayMap) {
      result.set(day, [...stationMap.values()]);
    }
    return result;
  }

  print() {
    this.printName();
    this.observations.forEach(o => {
      const t    = new Date(o.t).toISOString();
      const wind = o.dd != null ? `${o.dd}°/${o.ff}kn` : 'wind:n/a';
      const temp = o.tt   != null ? `${o.tt}°C`   : '';
      const slp  = o.mslp != null ? `${o.mslp}hPa` : '';
      console.log(`  ${t}  ${wind}  ${temp}  ${slp}`);
    });
  }

  printName() {
    console.log(`\n${this.id}  ${this.name ?? ''}  lat:${this.lat} lon:${this.lon}  (${this.observations.length} obs)`);
  }

}

//https://variable-variability.blogspot.com/2017/09/standard-two-letter-codes-for.html
export class Observation {
  constructor(t, dd, ff, tt = null, mslp = null, gust = null) {
    this.t     = t;     // time in ms
    this.dd    = dd;    // wind dir 0-360 true
    this.ff    = ff;    // wind speed (knots)
    this.tt    = tt;    // air temperature (°C)
    this.mslp  = mslp;  // mean sea level pressure (hPa)
    this.gust  = gust;  // wind gust (knots)
    this.color = ff != null ? windSpeedColors(Math.min(ff, 199)).alpha(0.5).hex() : null;
  }

  serialiseToObj() {
    return {
      t:this.t,
      dd:this.dd,
      ff:this.ff,
      tt:this.tt,
      mslp:this.mslp,
      gust:this.gust
    };
  }
}